import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const ThemeContext = createContext(null);
const STORAGE_KEY = 'tagedin-theme';

function applyDomTheme(theme) {
  const html = document.documentElement;
  if (theme === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
  html.dataset.theme = theme;
}

function initialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (e) {
    // localStorage unavailable (private mode / blocked) — fall through to default.
    if (typeof console !== 'undefined') console.warn('[theme] localStorage read failed', e);
  }
  return 'light';
}

export function ThemeProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState(() => initialTheme());

  useEffect(() => { applyDomTheme(theme); }, [theme]);

  // Once a user logs in, prefer their persisted theme (if any) over local pref.
  useEffect(() => {
    if (isAuthenticated && user?.theme && (user.theme === 'light' || user.theme === 'dark') && user.theme !== theme) {
      setThemeState(user.theme);
      try { localStorage.setItem(STORAGE_KEY, user.theme); } catch (e) {
        if (typeof console !== 'undefined') console.warn('[theme] persist failed', e);
      }
    }
  }, [isAuthenticated, user?.theme]); // eslint-disable-line

  const setTheme = useCallback(async (next) => {
    if (next !== 'light' && next !== 'dark') return;
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) {
      if (typeof console !== 'undefined') console.warn('[theme] persist failed', e);
    }
    if (isAuthenticated) {
      try { await api.patch('/settings', { theme: next }); } catch (e) {
        if (typeof console !== 'undefined') console.warn('[theme] sync to server failed', e);
      }
    }
  }, [isAuthenticated]);

  const toggle = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
